import React, { useCallback, useId, useRef, useState } from 'react';
import { trackHdrEvent } from '../services/analytics';

export interface BeforeAfterCompareProps {
  beforeSrc: string;
  afterSrc: string;
  beforeAlt: string;
  afterAlt: string;
  beforeLabel?: string;
  afterLabel?: string;
  width: number;
  height: number;
  caption?: string;
  priority?: boolean;
  placement?: string;
}

export default function BeforeAfterCompare({
  beforeSrc,
  afterSrc,
  beforeAlt,
  afterAlt,
  beforeLabel = 'Middle exposure',
  afterLabel = 'Merged HDR',
  width,
  height,
  caption,
  priority = false,
  placement = 'landing',
}: BeforeAfterCompareProps) {
  const [position, setPosition] = useState(50);
  const tracked = useRef(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const captionId = useId();

  const markInteracted = useCallback(() => {
    if (tracked.current) return;
    tracked.current = true;
    trackHdrEvent('hdr_before_after_interacted', { placement });
  }, [placement]);

  const setPositionSafe = useCallback(
    (next: number) => {
      setPosition(Math.max(0, Math.min(100, next)));
      markInteracted();
    },
    [markInteracted],
  );

  const setFromClientX = useCallback(
    (clientX: number) => {
      const el = frameRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0) return;
      setPositionSafe(((clientX - rect.left) / rect.width) * 100);
    },
    [setPositionSafe],
  );

  const onFramePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setFromClientX(event.clientX);
  };

  const onFramePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    setFromClientX(event.clientX);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Home') {
      event.preventDefault();
      setPositionSafe(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setPositionSafe(100);
    } else if (event.key === '0') {
      event.preventDefault();
      setPositionSafe(0);
    } else if (event.key === '5') {
      event.preventDefault();
      setPositionSafe(50);
    } else if (event.key === '1') {
      event.preventDefault();
      setPositionSafe(100);
    }
  };

  return (
    <figure className="ba-compare overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 shadow-2xl">
      <div
        ref={frameRef}
        className="ba-compare-frame relative w-full select-none overflow-hidden [cursor:ew-resize]"
        style={{ aspectRatio: `${width} / ${height}` }}
        onPointerDown={onFramePointerDown}
        onPointerMove={onFramePointerMove}
      >
        <img
          src={afterSrc}
          alt={afterAlt}
          width={width}
          height={height}
          decoding={priority ? 'sync' : 'async'}
          fetchPriority={priority ? 'high' : 'auto'}
          loading={priority ? 'eager' : 'lazy'}
          draggable={false}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />
        <span className="pointer-events-none absolute right-3 top-3 rounded-full border border-white/20 bg-black/60 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
          {afterLabel}
        </span>
        <div
          className="pointer-events-none absolute inset-0"
          style={{ clipPath: `inset(0 ${position}% 0 0)` }}
        >
          <img
            src={beforeSrc}
            alt={beforeAlt}
            width={width}
            height={height}
            decoding="async"
            loading={priority ? 'eager' : 'lazy'}
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <span className="absolute left-3 top-3 rounded-full border border-white/20 bg-black/60 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
            {beforeLabel}
          </span>
        </div>
        <div
          className="pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-white/85 shadow-[0_0_0_1px_rgba(15,23,42,0.35)]"
          style={{ left: `${position}%` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute top-1/2 z-10 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-slate-950/80 text-white"
          style={{ left: `${position}%` }}
          aria-hidden
        >
          <span className="text-xs" aria-hidden>
            {'\u25C2 \u25B8'}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
        <label className="sr-only" htmlFor={labelId}>
          Compare {beforeLabel} and {afterLabel}. Home is 0 percent, End is 100 percent.
        </label>
        <input
          id={labelId}
          type="range"
          min={0}
          max={100}
          step={1}
          value={position}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(position)}
          aria-describedby={caption ? captionId : undefined}
          onChange={(event) => setPositionSafe(Number(event.target.value))}
          onKeyDown={onKeyDown}
          className="ba-compare-range w-full accent-sky-400"
        />
        <div className="flex shrink-0 justify-center gap-2">
          {[
            { value: 0, label: '0%' },
            { value: 50, label: '50%' },
            { value: 100, label: '100%' },
          ].map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => setPositionSafe(preset.value)}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400 ${
                position === preset.value
                  ? 'border-sky-400/60 bg-sky-500/20 text-sky-200'
                  : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-500'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>
      {caption ? (
        <figcaption id={captionId} className="border-t border-slate-800 px-4 py-3 text-sm leading-relaxed text-slate-400">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
